/**
 * Synthetic Helene-class tropical storm threatening USCG Southeast District
 * assets. Cone geometry matches the CFA Helene demo extent (SE Atlantic /
 * FL–GA–SC) so the story lines up with Active Hurricanes / NHC cone demos.
 *
 * Asset names are cutters, stations, aircraft, and teams — not Sector / Area
 * AOR hierarchy labels.
 */

export type UscgFocDemoAsset = {
  id: string;
  name: string;
  unitType:
    | 'Cutter'
    | 'Boat Station'
    | 'Aircraft'
    | 'Response Team'
    | 'Aids to Navigation';
  city: string;
  state: string;
  lon: number;
  lat: number;
  inCone: boolean;
};

/**
 * Approximate NHC-style forecast cone (outer ring) for a Helene-class approach
 * toward the Southeast District AOR.
 */
export const USCG_FOC_SE_CONE_RING: Array<[number, number]> = [
  [-90.2, 23.5],
  [-88.5, 24.0],
  [-86.0, 25.2],
  [-84.0, 26.8],
  [-82.5, 28.5],
  [-81.8, 30.2],
  [-81.5, 31.8],
  [-81.8, 33.2],
  [-82.5, 34.5],
  [-84.0, 35.2],
  [-86.0, 35.0],
  [-87.5, 34.0],
  [-88.8, 32.5],
  [-89.5, 30.5],
  [-90.0, 28.0],
  [-90.5, 25.5],
  [-90.2, 23.5],
];

export const USCG_FOC_SE_STORM_LAYER_NAME =
  'USCG FOC — Helene forecast cone (Southeast District)';

export const USCG_FOC_SE_STORM_LAYER_DESCRIPTION =
  'Synthetic tropical-storm forecast cone for the USCG FOC Requirements demo. Not for navigation.';

export const USCG_FOC_SE_STORM_POLYGON_COLOR = '#f59e0b';
export const USCG_FOC_SE_STORM_POLYGON_OPACITY = 0.28;

/** Hub map GraphicsLayer for FOC SE cone assets (Equinor/CFA-style pins). */
export const USCG_FOC_SE_ASSETS_LAYER_ID = 'uscg-foc-se-assets';
export const USCG_FOC_SE_ASSETS_LAYER_TITLE =
  'USCG Southeast assets (Helene demo)';

export type UscgFocSeAdvisoryPoint = {
  id: string;
  label: string;
  datetime: string;
  lon: number;
  lat: number;
  maxWindMph: number;
  category: 'TD' | 'TS' | 'H1' | 'H2' | 'H3' | 'H4' | 'H5';
};

export const USCG_FOC_SE_ADVISORIES: UscgFocSeAdvisoryPoint[] = [
  {
    id: 'foc-se-adv-1',
    label: 'Tropical Depression Nine',
    datetime: '2024-09-22T18:00:00.000Z',
    lon: -82.8,
    lat: 15.2,
    maxWindMph: 35,
    category: 'TD',
  },
  {
    id: 'foc-se-adv-2',
    label: 'Tropical Storm Helene',
    datetime: '2024-09-24T06:00:00.000Z',
    lon: -84.5,
    lat: 17.8,
    maxWindMph: 60,
    category: 'TS',
  },
  {
    id: 'foc-se-adv-3',
    label: 'Tropical Storm Helene',
    datetime: '2024-09-25T12:00:00.000Z',
    lon: -85.8,
    lat: 22.4,
    maxWindMph: 70,
    category: 'TS',
  },
];

/**
 * Plausible USCG platforms / units inside the forecast cone — not Area/District/
 * Sector AOR names from the FOC AOR catalog.
 */
export const USCG_FOC_SE_ASSETS: UscgFocDemoAsset[] = [
  {
    id: 'foc-se-cgc-kathleen-moore',
    name: 'CGC Kathleen Moore (WPC-110)',
    unitType: 'Cutter',
    city: 'Key West',
    state: 'FL',
    lon: -81.78,
    lat: 24.56,
    inCone: true,
  },
  {
    id: 'foc-se-cgc-bernard-webber',
    name: 'CGC Bernard C. Webber (WPC-1101)',
    unitType: 'Cutter',
    city: 'Miami Beach',
    state: 'FL',
    lon: -80.13,
    lat: 25.79,
    inCone: true,
  },
  {
    id: 'foc-se-cgc-confidence',
    name: 'CGC Confidence (WMEC-619)',
    unitType: 'Cutter',
    city: 'Cape Canaveral',
    state: 'FL',
    lon: -80.6,
    lat: 28.4,
    inCone: true,
  },
  {
    id: 'foc-se-station-fort-pierce',
    name: 'Station Fort Pierce',
    unitType: 'Boat Station',
    city: 'Fort Pierce',
    state: 'FL',
    lon: -80.32,
    lat: 27.47,
    inCone: true,
  },
  {
    id: 'foc-se-station-yankeetown',
    name: 'Station Yankeetown',
    unitType: 'Boat Station',
    city: 'Yankeetown',
    state: 'FL',
    lon: -82.72,
    lat: 29.03,
    inCone: true,
  },
  {
    id: 'foc-se-mh60-clearwater',
    name: 'MH-60T 6017 (Air Station Clearwater)',
    unitType: 'Aircraft',
    city: 'Clearwater',
    state: 'FL',
    lon: -82.69,
    lat: 27.91,
    inCone: true,
  },
  {
    id: 'foc-se-msst-miami',
    name: 'MSST 91114 Miami',
    unitType: 'Response Team',
    city: 'Miami',
    state: 'FL',
    lon: -80.19,
    lat: 25.76,
    inCone: true,
  },
  {
    id: 'foc-se-ant-jacksonville',
    name: 'ANT Jacksonville',
    unitType: 'Aids to Navigation',
    city: 'Jacksonville',
    state: 'FL',
    lon: -81.66,
    lat: 30.33,
    inCone: true,
  },
];

export function assetsInCone(): UscgFocDemoAsset[] {
  return USCG_FOC_SE_ASSETS.filter((asset) => asset.inCone);
}

export function findUscgFocSeAsset(
  assetId: string | undefined
): UscgFocDemoAsset | undefined {
  if (!assetId) return undefined;
  return USCG_FOC_SE_ASSETS.find((asset) => asset.id === assetId);
}

export function formatUscgFocAssetLabel(asset: UscgFocDemoAsset): string {
  return `${asset.name} (${asset.city}, ${asset.state})`;
}
